"use client"
import IconChevronDown from "@/app/(site)/_components/Atoms/Icons/IconChevronDown"
import IconChevronUp from "@/app/(site)/_components/Atoms/Icons/IconChevronUp"
import IconMinus from "@/app/(site)/_components/Atoms/Icons/IconMinus"
import IconPlus from "@/app/(site)/_components/Atoms/Icons/IconPlus"
import Text from "@/app/(site)/_components/Atoms/Text"
import ArticleGrid from "@/app/(site)/_components/Modules/ArticleListing/Grid"
import FilterOptionGroup from "@/app/(site)/_components/Modules/ArticleListing/SortFiltering/FilterOptionGroup"
import {
    filterArticles,
    sortArticles
} from "@/app/(site)/_components/Modules/ArticleListing/SortFiltering/helper"
import ButtonText from "@/app/(site)/_components/Molecules/ButtonText"
import { useClickOutside } from "@/app/(site)/_utils/_hooks/useOutsideIdentifier"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { getLangCode } from "@/src/utils/string/getLangCode"
import { t } from "@/translations"
import { type JournalPageType } from "@/types"
import { useRouter } from "next/navigation"
import React, { useEffect, useRef, useState } from "react"

type Props = {
    pageData: JournalPageType | undefined
    params: { locale: string }
}

const CATEGORY_TRIGGER_ID = "journal-by-category-trigger"
const SORT_TRIGGER_ID = "journal-sort-by-trigger"
const MOBILE_TRIGGER_ID = "journal-filter-and-sort-trigger"
const MOBILE_SORT_HEADING_ID = "journal-mobile-sort-heading"
const MOBILE_FILTER_HEADING_ID = "journal-mobile-filter-heading"

const JournalGrid: React.FC<Props> = ({ pageData, params }) => {
    const langCode = getLangCode(params.locale) ?? "en"
    const [openSortBy, setOpenSortBy] = useState(false)
    const [openFilter, setOpenFilter] = useState(false)
    const [openMobileFilterAndSort, setOpenMobileFilterAndSort] = useState(false)
    const [selectedSortOption, setSelectedSortOption] = useState<string>("newest")
    const [selectedFilterOption, setSelectedFilterOption] = useState<string>("all")
    const router = useRouter()
    const sortRef = useRef<HTMLDivElement>(null)
    const filterRef = useRef<HTMLDivElement>(null)
    const allCategories =
        pageData?.articleCategories &&
        pageData.articleCategories.map(category => category.categoryTitle.toLowerCase())
    const filterOptions = [...new Set(allCategories)].map(category => ({
        value: category,
        label: category
    }))
    const sortOptions = [
        { value: "newest", label: t(langCode, "journal.newest") },
        { value: "oldest", label: t(langCode, "journal.oldest") },
        { value: "alphabetical", label: t(langCode, "journal.alphabetical") }
    ]
    const categoryOptions = [
        { value: "all", label: t(langCode, "journal.all") },
        ...filterOptions
    ]

    useClickOutside({
        refs: [sortRef, filterRef],
        onOutsideClick: () => {
            // Pointer dismissal: close without pulling focus back to the trigger.
            setOpenSortBy(false)
            setOpenFilter(false)
        }
    })

    // Set the selected sort and filter options based on the query params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const sortOption = params.get("sort") || "newest"
        const filterOption = params.get("category") || "all"
        setSelectedSortOption(sortOption)
        setSelectedFilterOption(filterOption)
    }, [])

    const returnFocusTo = (triggerId: string) => {
        document.getElementById(triggerId)?.focus()
    }

    // Handle sort option selection
    const applySortOption = (value: string) => {
        setSelectedSortOption(value)
        const params = new URLSearchParams(window.location.search)
        if (value) {
            params.set("sort", value)
        } else {
            params.delete("sort")
        }
        router.push(`?${params.toString()}`, { scroll: false })
    }

    // Handle filter option selection
    const applyFilterOption = (value: string) => {
        setSelectedFilterOption(value)
        const params = new URLSearchParams(window.location.search)
        if (value === "all") {
            params.delete("category")
        } else {
            params.set("category", value)
        }
        router.push(`?${params.toString()}`, { scroll: false })
    }

    const closeSortMenu = () => {
        setOpenSortBy(false)
        returnFocusTo(SORT_TRIGGER_ID)
    }

    const closeFilterMenu = () => {
        setOpenFilter(false)
        returnFocusTo(CATEGORY_TRIGGER_ID)
    }

    const closeMobilePanel = () => {
        setOpenMobileFilterAndSort(false)
        returnFocusTo(MOBILE_TRIGGER_ID)
    }

    // Sort and filter articles based on selected options
    const sortedArticles = sortArticles(pageData?.articles, selectedSortOption)

    const filteredArticles = filterArticles(
        sortedArticles,
        selectedFilterOption,
        selectedSortOption
    )

    // Extract the number of articles
    const articleNumber = filteredArticles?.length
    const articleNumberString = `${articleNumber === 1 ? articleNumber + " article" : articleNumber + " articles"} `

    return (
        <div className="mx-16 md:mx-24">
            {/* Positioned above the grid below it: the article images are
                themselves positioned, so they painted over the 2px the trigger
                focus ring sits outside its box, clipping it along the bottom. */}
            <div className="relative z-10 mb-6 grid grid-cols-6">
                <div className="col-span-3 flex gap-12 md:col-span-2">
                    <div className="fled md:hidden">
                        <ButtonText
                            id={MOBILE_TRIGGER_ID}
                            type="button"
                            size="big"
                            aria-expanded={openMobileFilterAndSort}
                            icon={
                                openMobileFilterAndSort ? (
                                    <IconMinus className="h-16 w-16" />
                                ) : (
                                    <IconPlus className="h-16 w-16" />
                                )
                            }
                            iconPosition="right"
                            className={clsx(
                                "flex items-center gap-4 p-14 pl-0",
                                FOCUS_VISIBLE_CLASS
                            )}
                            onClick={() => {
                                setOpenMobileFilterAndSort(!openMobileFilterAndSort)
                            }}
                        >
                            {t(langCode, "journal.filter_and_sort")}
                        </ButtonText>
                    </div>
                    {/* Each panel follows its own trigger so tabbing out of it
                        lands on the next control rather than skipping past it. */}
                    <div className="relative hidden md:flex">
                        <ButtonText
                            id={CATEGORY_TRIGGER_ID}
                            type="button"
                            size="big"
                            aria-haspopup="true"
                            aria-expanded={openFilter}
                            icon={
                                openFilter ? (
                                    <IconMinus className="h-16 w-16" />
                                ) : (
                                    <IconPlus className="h-16 w-16" />
                                )
                            }
                            iconPosition="right"
                            onClick={() => {
                                setOpenFilter(!openFilter)
                                setOpenSortBy(false)
                            }}
                            className={clsx(
                                "flex items-center gap-4 p-14 pl-0",
                                FOCUS_VISIBLE_CLASS
                            )}
                        >
                            {t(langCode, "journal.by_category")}
                        </ButtonText>
                        {openFilter && (
                            <div
                                ref={filterRef}
                                className="absolute left-0 top-full z-10 hidden w-300 bg-white p-16 md:flex"
                            >
                                <FilterOptionGroup
                                    options={categoryOptions}
                                    selectedValue={selectedFilterOption}
                                    labelledBy={CATEGORY_TRIGGER_ID}
                                    onSelect={value => {
                                        applyFilterOption(value)
                                        closeFilterMenu()
                                    }}
                                    onDismiss={closeFilterMenu}
                                    onFocusLeave={() => setOpenFilter(false)}
                                    autoFocus
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-span-3 flex items-center justify-end text-center md:col-span-2 md:justify-center">
                    <Text size="medium" weight="regular" className="text-[#767676]">
                        {articleNumberString}
                    </Text>
                </div>

                <div className="relative col-span-2 hidden justify-end md:flex">
                    <ButtonText
                        id={SORT_TRIGGER_ID}
                        type="button"
                        size="big"
                        aria-haspopup="true"
                        aria-expanded={openSortBy}
                        icon={
                            openSortBy ? (
                                <IconChevronUp className="h-16 w-16" />
                            ) : (
                                <IconChevronDown className="h-16 w-16" />
                            )
                        }
                        onClick={() => {
                            setOpenSortBy(!openSortBy)
                            setOpenFilter(false)
                        }}
                        iconPosition="right"
                        className={clsx(
                            "flex items-center gap-4 p-14 pl-0",
                            FOCUS_VISIBLE_CLASS
                        )}
                    >
                        {t(langCode, "journal.sort_by")}
                    </ButtonText>
                    {openSortBy && (
                        <div
                            ref={sortRef}
                            className="absolute right-0 top-full z-10 hidden w-300 bg-white p-16 md:flex"
                        >
                            <FilterOptionGroup
                                options={sortOptions}
                                selectedValue={selectedSortOption}
                                labelledBy={SORT_TRIGGER_ID}
                                onSelect={value => {
                                    applySortOption(value)
                                    closeSortMenu()
                                }}
                                onDismiss={closeSortMenu}
                                onFocusLeave={() => setOpenSortBy(false)}
                                autoFocus
                            />
                        </div>
                    )}
                </div>
            </div>
            {openMobileFilterAndSort && (
                <div className="md:hidden">
                    <div className="w-300 bg-white py-16">
                        <Text
                            id={MOBILE_SORT_HEADING_ID}
                            size="medium"
                            weight="regular"
                            className="mb-16"
                        >
                            {t(langCode, "journal.sort")}
                        </Text>

                        <FilterOptionGroup
                            options={sortOptions}
                            selectedValue={selectedSortOption}
                            labelledBy={MOBILE_SORT_HEADING_ID}
                            onSelect={applySortOption}
                            onDismiss={closeMobilePanel}
                        />
                    </div>
                    <div className="w-300 bg-white py-16">
                        <Text
                            id={MOBILE_FILTER_HEADING_ID}
                            size="medium"
                            weight="regular"
                            className="mb-16"
                        >
                            {t(langCode, "journal.filter")}
                        </Text>
                        <FilterOptionGroup
                            options={categoryOptions}
                            selectedValue={selectedFilterOption}
                            labelledBy={MOBILE_FILTER_HEADING_ID}
                            onSelect={applyFilterOption}
                            onDismiss={closeMobilePanel}
                        />
                    </div>
                </div>
            )}
            {filteredArticles && (
                <ArticleGrid articles={filteredArticles} locale={params.locale} />
            )}
        </div>
    )
}

export default JournalGrid
